import React, { useState, useEffect, useMemo, useContext } from 'react'
import PropTypes from 'prop-types'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons'
import ActionButton from 'shared/buttons/action-button'
import UserContext from 'context/user-context'
import { useReviewStatuses } from 'api/hooks/use-review-statuses'
import useMonitoringReview from './use-monitoring-review'
import useCreateMonitoringExclusions from './use-create-monitoring-exclusions'
import useTransactionEmailDraft from './use-transaction-email-draft'

const FALSE_POSITIVE_REASON = 'False Positive'

const IGNORE_REASONS = [
  FALSE_POSITIVE_REASON,
  'Pre-Approved Exception',
  'Other'
]

const RESOLVE_REASONS = [
  'Fixed',
  'Approved Exception',
  'Vendor Contacted',
  'Other'
]

function IgnoreModal({
  show,
  onHide,
  onConfirm,
  isLoading,
  stopPropagation,
  sectionAbbreviation,
  vendorId,
  vendorName,
  employeeId,
  employeeName,
  controlAnalytics
}) {
  const [selectedReason, setSelectedReason] = useState(null)
  const [otherText, setOtherText] = useState('')
  const [selectedAnalyticIds, setSelectedAnalyticIds] = useState([])
  const [excludeVendor, setExcludeVendor] = useState(false)
  const [excludeEmployee, setExcludeEmployee] = useState(false)

  const isFalsePositive = selectedReason === FALSE_POSITIVE_REASON
  const isPcard = sectionAbbreviation === 'PC'
  const canExcludeVendor = Boolean(vendorId)
  const canExcludeEmployee = isPcard && Boolean(employeeId)
  const hasControlAnalytics = (controlAnalytics || []).length > 0

  const resetState = () => {
    setSelectedReason(null)
    setOtherText('')
    setSelectedAnalyticIds([])
    setExcludeVendor(false)
    setExcludeEmployee(false)
  }

  const handleHide = () => {
    resetState()
    onHide()
  }

  const toggleAnalytic = (analyticId) => {
    setSelectedAnalyticIds((prev) =>
      prev.includes(analyticId)
        ? prev.filter((id) => id !== analyticId)
        : [...prev, analyticId]
    )
  }

  const handleConfirm = () => {
    if (!selectedReason) return
    const reason = selectedReason === 'Other' ? otherText.trim() : selectedReason
    if (!reason) return

    const exclusions = []
    if (isFalsePositive && selectedAnalyticIds.length > 0) {
      if (excludeVendor && canExcludeVendor) {
        exclusions.push({
          excludableType: 'Vendor',
          excludableId: vendorId,
          analyticIds: selectedAnalyticIds
        })
      }
      if (excludeEmployee && canExcludeEmployee) {
        exclusions.push({
          excludableType: 'Employee',
          excludableId: employeeId,
          analyticIds: selectedAnalyticIds
        })
      }
    }

    onConfirm({ reason, exclusions })
    resetState()
  }

  const showExcludeSection = isFalsePositive
  const hasEligibleTargets = canExcludeVendor || canExcludeEmployee
  const targetChecked = excludeVendor || excludeEmployee
  const analyticChecked = selectedAnalyticIds.length > 0
  const partialExclusionSelection =
    (targetChecked && !analyticChecked) || (analyticChecked && !targetChecked)

  return (
    <Modal show={show} onHide={handleHide} centered onClick={stopPropagation}>
      <Modal.Header closeButton>
        <Modal.Title>Ignore transaction</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-2">
          Pick a reason — this will be recorded on the review.
        </p>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {IGNORE_REASONS.map((reason) => (
            <Button
              key={reason}
              size="sm"
              variant={selectedReason === reason ? 'primary' : 'light'}
              onClick={() => setSelectedReason(reason)}
            >
              {reason}
            </Button>
          ))}
        </div>
        {selectedReason === 'Other' && (
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted">
              Describe the reason
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder="Add context for this review"
              autoFocus
            />
          </Form.Group>
        )}

        {showExcludeSection && (
          <div
            className="border rounded p-3"
            style={{ background: '#f8fafc' }}
          >
            <div className="fw-bold small text-dark mb-2">
              Also exclude from future monitoring
            </div>
            <p className="text-muted small mb-2">
              {isPcard
                ? 'Optionally stop flagging this vendor and/or employee on specific control analytics.'
                : 'Optionally stop flagging this vendor on specific control analytics.'}
              {' '}
              Existing open matches will be auto-ignored.
            </p>

            {!hasControlAnalytics && (
              <div className="text-muted small fst-italic">
                No control-tagged analytics on this transaction to exclude.
              </div>
            )}

            {hasControlAnalytics && !hasEligibleTargets && (
              <div className="text-muted small fst-italic">
                {isPcard
                  ? 'This transaction has no vendor or eligible employee to exclude.'
                  : 'This transaction has no vendor to exclude.'}
              </div>
            )}

            {hasControlAnalytics && hasEligibleTargets && (
              <>
                <Form.Label className="small text-muted mb-1">
                  Apply exclusion to
                </Form.Label>
                <div className="mb-3">
                  {canExcludeVendor && (
                    <Form.Check
                      type="checkbox"
                      id="exclude-vendor"
                      label={`Vendor: ${vendorName || 'Unknown'}`}
                      checked={excludeVendor}
                      onChange={(e) => setExcludeVendor(e.target.checked)}
                    />
                  )}
                  {canExcludeEmployee && (
                    <Form.Check
                      type="checkbox"
                      id="exclude-employee"
                      label={`Employee: ${employeeName || 'Unknown'}`}
                      checked={excludeEmployee}
                      onChange={(e) => setExcludeEmployee(e.target.checked)}
                    />
                  )}
                </div>

                <Form.Label className="small text-muted mb-1">
                  For these analytics
                </Form.Label>
                <div>
                  {controlAnalytics.map((analytic) => (
                    <Form.Check
                      key={analytic.id}
                      type="checkbox"
                      id={`exclude-analytic-${analytic.id}`}
                      label={analytic.name}
                      checked={selectedAnalyticIds.includes(analytic.id)}
                      onChange={() => toggleAnalytic(analytic.id)}
                    />
                  ))}
                </div>

                {partialExclusionSelection && (
                  <div className="text-danger small mt-2">
                    {targetChecked
                      ? 'Select at least one analytic to apply the exclusion to.'
                      : 'Pick a vendor or employee to apply this exclusion to.'}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={handleHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={
            !selectedReason ||
            (selectedReason === 'Other' && !otherText.trim()) ||
            partialExclusionSelection ||
            isLoading
          }
          onClick={handleConfirm}
        >
          Ignore transaction
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function ReasonModal({
  show,
  onHide,
  title,
  reasons,
  confirmLabel,
  onConfirm,
  isLoading,
  stopPropagation
}) {
  const [selectedReason, setSelectedReason] = useState(null)
  const [otherText, setOtherText] = useState('')

  const handleConfirm = () => {
    if (!selectedReason) return
    const reason =
      selectedReason === 'Other' ? otherText.trim() : selectedReason
    if (!reason) return
    onConfirm(reason)
    setSelectedReason(null)
    setOtherText('')
  }

  const handleHide = () => {
    setSelectedReason(null)
    setOtherText('')
    onHide()
  }

  return (
    <Modal show={show} onHide={handleHide} centered onClick={stopPropagation}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-2">
          Pick a reason — this will be recorded on the review.
        </p>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {reasons.map((reason) => (
            <Button
              key={reason}
              size="sm"
              variant={
                selectedReason === reason ? 'primary' : 'light'
              }
              onClick={() => setSelectedReason(reason)}
            >
              {reason}
            </Button>
          ))}
        </div>
        {selectedReason === 'Other' && (
          <Form.Group>
            <Form.Label className="small text-muted">
              Describe the reason
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder="Add context for this review"
              autoFocus
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={handleHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={
            !selectedReason ||
            (selectedReason === 'Other' && !otherText.trim()) ||
            isLoading
          }
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

function CopyButton({ text, label = 'Copy', disabled }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (_err) {
      // Clipboard API unavailable (e.g. non-secure context) — silently no-op;
      // the user can still select the text manually.
    }
  }

  return (
    <Button
      size="sm"
      variant="light"
      className="text-primary"
      onClick={handleCopy}
      disabled={disabled || !text}
    >
      <FontAwesomeIcon
        icon={copied ? faCheck : faCopy}
        className={`me-1 ${copied ? 'text-success' : ''}`}
      />
      {copied ? 'Copied' : label}
    </Button>
  )
}

CopyButton.propTypes = {
  text: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool
}

function EmailDraftModal({
  show,
  onHide,
  stopPropagation,
  isLoading,
  error,
  draft,
  onRetry
}) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')

  // Seed the editable fields once the AI draft arrives (and reset on re-draft).
  useEffect(() => {
    setSubject(draft?.subject || '')
    setBody(draft?.body || '')
  }, [draft])

  return (
    <Modal show={show} onHide={onHide} centered size="lg" onClick={stopPropagation}>
      <Modal.Header closeButton>
        <Modal.Title>Draft email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="d-flex align-items-center gap-2 text-muted py-4 justify-content-center">
            <Spinner animation="border" size="sm" />
            <span>Drafting email…</span>
          </div>
        )}

        {!isLoading && error && (
          <div className="text-danger py-3">
            <p className="mb-2">Sorry, we couldn’t draft the email.</p>
            <Button size="sm" variant="outline-primary" onClick={onRetry}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !error && draft && (
          <>
            <p className="text-muted small mb-3">
              AI-generated draft based on this transaction and your policies.
              Review and edit before sending — then copy it into your email or Teams.
            </p>
            <Form.Group className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-1">
                <Form.Label className="small text-muted mb-0">Subject</Form.Label>
                <CopyButton text={subject} label="Copy Subject" />
              </div>
              <Form.Control
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <div className="d-flex align-items-center gap-2 mb-1">
                <Form.Label className="small text-muted mb-0">Body</Form.Label>
                <CopyButton text={body} label="Copy Body" />
              </div>
              <Form.Control
                as="textarea"
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

EmailDraftModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  stopPropagation: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.bool,
  draft: PropTypes.shape({
    subject: PropTypes.string,
    body: PropTypes.string
  }),
  onRetry: PropTypes.func
}

function MonitoringReviewActions({
  transactionId,
  review,
  size = 'sm',
  compact = false,
  onChange,
  sectionId,
  sectionAbbreviation,
  vendorId,
  vendorName,
  employeeId,
  employeeName,
  controlAnalytics = [],
  highRisk = false,
  // Review surfaces settle against the record, not by writing to the cardholder.
  allowEmailDraft = true,
  // The compare/evidence is the record of why, so no reason dialog.
  resolveWithoutReason = false,
  // Decides every side of the duplicate, not just this record.
  duplicateAnalyticId = null
}) {
  const [ignoreModalOpen, setIgnoreModalOpen] = useState(false)
  const [resolveModalOpen, setResolveModalOpen] = useState(false)
  const [emailModalOpen, setEmailModalOpen] = useState(false)

  const user = useContext(UserContext)
  const aiEnabled = user?.municipality?.setting?.ai_search_enabled

  const {
    mutate: draftEmail,
    data: emailDraft,
    isLoading: isDraftingEmail,
    isError: emailDraftError,
    reset: resetEmailDraft
  } = useTransactionEmailDraft()

  const openEmailDraft = () => {
    resetEmailDraft()
    setEmailModalOpen(true)
    draftEmail({ transactionId, highRisk })
  }

  const { data: statuses } = useReviewStatuses({
    params: { context: 'monitoring' },
    options: { staleTime: 5 * 60 * 1000 }
  })

  const statusIdByName = useMemo(() => {
    const map = {}
    ;(statuses || []).forEach((s) => {
      map[s.name] = s.id
    })
    return map
  }, [statuses])

  const { mutate, isLoading } = useMonitoringReview({
    options: {
      onSuccess: (data, variables) => {
        if (onChange) {
          onChange({
            id: data?.id || review?.id,
            status: variables._statusName,
            reason: variables.reason,
            // Every record the decision reached — more than one for a duplicate.
            transactionIds: data?.reviews?.map((entry) => entry.transaction_id) || [transactionId]
          })
        }
      }
    }
  })

  const { mutate: createExclusions, isLoading: isCreatingExclusions } =
    useCreateMonitoringExclusions()

  const applyStatus = (statusName, reason = null) => {
    const reviewStatusId = statusIdByName[statusName]
    if (!reviewStatusId) return
    mutate({
      transactionId,
      reviewStatusId,
      reason,
      reviewId: review?.id,
      duplicateAnalyticId,
      _statusName: statusName
    })
  }

  const currentStatus = review?.status || 'Open'
  const isOpen = currentStatus === 'Open'

  const stopPropagation = (e) => e.stopPropagation()

  const busy = isLoading || isCreatingExclusions

  return (
    <>
      <div
        className={`d-flex gap-1 ${compact ? '' : 'flex-wrap'}`}
        onClick={stopPropagation}
        onKeyDown={stopPropagation}
        role="presentation"
      >
        {aiEnabled && allowEmailDraft && (
          <ActionButton
            size={size}
            variant="light"
            className="text-primary"
            icon={faEnvelope}
            label="Draft email"
            loading={isDraftingEmail}
            loadingLabel="Drafting…"
            disabled={busy}
            onClick={openEmailDraft}
          />
        )}
        {!isOpen && (
          <Button
            size={size}
            variant="light"
            className="text-primary"
            disabled={busy}
            onClick={() => applyStatus('Open')}
          >
            Re-open
          </Button>
        )}
        {currentStatus !== 'Ignored' && (
          <Button
            size={size}
            variant="light"
            disabled={busy}
            onClick={() => setIgnoreModalOpen(true)}
          >
            Ignore
          </Button>
        )}
        {currentStatus !== 'Resolved' && (
          <Button
            size={size}
            variant="primary"
            disabled={busy}
            onClick={() =>
              resolveWithoutReason ? applyStatus('Resolved') : setResolveModalOpen(true)
            }
          >
            Mark as Resolved
          </Button>
        )}
      </div>

      <IgnoreModal
        show={ignoreModalOpen}
        onHide={() => setIgnoreModalOpen(false)}
        isLoading={busy}
        stopPropagation={stopPropagation}
        sectionAbbreviation={sectionAbbreviation}
        vendorId={vendorId}
        vendorName={vendorName}
        employeeId={employeeId}
        employeeName={employeeName}
        controlAnalytics={controlAnalytics}
        onConfirm={({ reason, exclusions }) => {
          applyStatus('Ignored', reason)
          exclusions.forEach((exclusion) => {
            createExclusions({
              sectionId,
              analyticIds: exclusion.analyticIds,
              excludableType: exclusion.excludableType,
              excludableId: exclusion.excludableId,
              reason
            })
          })
          setIgnoreModalOpen(false)
        }}
      />

      <ReasonModal
        show={resolveModalOpen}
        onHide={() => setResolveModalOpen(false)}
        title="Resolve transaction"
        reasons={RESOLVE_REASONS}
        confirmLabel="Resolve transaction"
        isLoading={busy}
        stopPropagation={stopPropagation}
        onConfirm={(reason) => {
          applyStatus('Resolved', reason)
          setResolveModalOpen(false)
        }}
      />

      <EmailDraftModal
        show={emailModalOpen}
        onHide={() => setEmailModalOpen(false)}
        stopPropagation={stopPropagation}
        isLoading={isDraftingEmail}
        error={emailDraftError}
        draft={emailDraft}
        onRetry={() => draftEmail({ transactionId })}
      />
    </>
  )
}

MonitoringReviewActions.propTypes = {
  transactionId: PropTypes.string.isRequired,
  review: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    reason: PropTypes.string
  }),
  size: PropTypes.string,
  compact: PropTypes.bool,
  allowEmailDraft: PropTypes.bool,
  resolveWithoutReason: PropTypes.bool,
  duplicateAnalyticId: PropTypes.string,
  onChange: PropTypes.func,
  sectionId: PropTypes.string,
  sectionAbbreviation: PropTypes.string,
  vendorId: PropTypes.string,
  vendorName: PropTypes.string,
  employeeId: PropTypes.string,
  employeeName: PropTypes.string,
  controlAnalytics: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ),
  highRisk: PropTypes.bool
}

export default MonitoringReviewActions
export { IGNORE_REASONS, RESOLVE_REASONS, FALSE_POSITIVE_REASON }
